import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { getBalloonById, updateBalloon } from "../../common/api/baloonsApi";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import CalendarSelectFormik from "../../shared/Ui/Calendar/CalendarSelectFormik";
import style from "../GasCilinders/GasCilinders.module.css";
import { FaExclamationCircle } from "react-icons/fa";

function GasCilinder() {
  const navigate = useNavigate();
  const { gasCilinderId } = useParams();
  const { t } = useTranslation();
  const [balloon, setBalloon] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!gasCilinderId) return;
    getBalloonById(gasCilinderId)
      .then((data) => {
        setBalloon(data?.balloon ?? data ?? null);
      })
      .catch(() => {
        setBalloon(null);
      });
  }, [gasCilinderId]);

  const currentMonth = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  }, []);

  const validationSchema = useMemo(() => {
    const isMonthString = (value?: string) => {
      if (!value) return false;
      return /^\d{4}-\d{2}$/.test(value);
    };
    const compareMonth = (a?: string, b?: string) => {
      if (!a || !b) return 0;
      const [ay, am] = a.split("-").map((v) => parseInt(v, 10));
      const [by, bm] = b.split("-").map((v) => parseInt(v, 10));
      return ay === by ? am - bm : ay - by;
    };
    const currentMonthStr = currentMonth;

    return Yup.object().shape({
      factoryNumber: Yup.number()
        .typeError("Обязательное поле")
        .integer("Должно быть целым числом")
        .min(0, "Не может быть отрицательным")
        .required("Обязательное поле"),
      balloonCode: Yup.number()
        .typeError("Обязательное поле")
        .integer("Должно быть целым числом")
        .min(0, "Не может быть отрицательным")
        .required("Обязательное поле"),
      emptyWeight: Yup.number()
        .typeError("Обязательное поле")
        .required("Обязательное поле")
        .min(3, "Минимум 3,0 кг")
        .max(27, "Максимум 27,0 кг")
        .test("one-decimal", "Только одно десятичное значение", (value) => {
          if (value === undefined || value === null) return false;
          const num = Number(value);
          if (Number.isNaN(num)) return false;
          return Number.isInteger(Math.round(num * 10));
        }),
      gasWeight: Yup.number()
        .typeError("Обязательное поле")
        .required("Обязательное поле")
        .min(13, "Минимум 13,0 кг")
        .max(50, "Максимум 50,0 кг")
        .test("one-decimal", "Только одно десятичное значение", (value) => {
          if (value === undefined || value === null) return false;
          const num = Number(value);
          if (Number.isNaN(num)) return false;
          return Number.isInteger(Math.round(num * 10));
        }),
      material: Yup.string().required("Обязательное поле"),
      country: Yup.string().nullable(),
      productionDate: Yup.string()
        .required("Обязательное поле")
        .test("format-month", "Формат ММ-ГГГГ (YYYY-MM)", (value) => isMonthString(value as string))
        .test("not-future", "Будущая дата запрещена", (value) => {
          if (!value) return false;
          return compareMonth(value, currentMonthStr) <= 0;
        }),
      workingPressure: Yup.number()
        .typeError("Обязательное поле")
        .required("Обязательное поле")
        .min(0, "Не может быть отрицательным")
        .test("lte-testPressure", "Рабочее давление должно быть ≤ испытательного", function (value) {
          const testPressure = this.parent?.testPressure;
          if (value === undefined || value === null || testPressure === undefined || testPressure === null) return true;
          return Number(value) <= Number(testPressure);
        }),
      testPressure: Yup.number()
        .typeError("Обязательное поле")
        .required("Обязательное поле")
        .min(0, "Не может быть отрицательным")
        .test("gte-workingPressure", "Испытательное давление должно быть ≥ рабочего", function (value) {
          const workingPressure = this.parent?.workingPressure;
          if (value === undefined || value === null || workingPressure === undefined || workingPressure === null) return true;
          return Number(value) >= Number(workingPressure);
        }),
      lastInspectionDate: Yup.string()
        .required("Обязательное поле"),
      lastRepairDate: Yup.string().nullable(),
      repairResponsible: Yup.string().nullable(),
      nextInspectionDate: Yup.string()
        .required("Обязательное поле")
        .test("format-month", "Формат ММ-ГГГГ (YYYY-MM)", (value) => isMonthString(value as string))
        .test("gt-lastInspection", "Дата следующей поверки должна быть больше даты последней поверки", function (value) {
          const lastInspection = this.parent?.lastInspectionDate as string | undefined;
          if (!value || !lastInspection) return true;
          return compareMonth(value, lastInspection) > 0;
        }),
    });
  }, [currentMonth]);

  const initialEditValues = useMemo(() => {
    return {
      factoryNumber: balloon?.factoryNumber ?? null,
      balloonCode: balloon?.balloonCode ?? null,
      emptyWeight: balloon?.emptyWeight ?? null,
      gasWeight: balloon?.gasWeight ?? null,
      material: balloon?.material ?? "STEEL",
      country: balloon?.country ?? "KAZAKHSTAN",
      productionDate: balloon?.productionDate ?? null,
      workingPressure: balloon?.workingPressure ?? null,
      testPressure: balloon?.testPressure ?? null,
      lastInspectionDate: balloon?.lastInspectionDate ?? null,
      lastRepairDate: balloon?.lastRepairDate ?? null,
      repairResponsible: balloon?.repairResponsible ?? "",
      nextInspectionDate: balloon?.nextInspectionDate ?? null,
    };
  }, [balloon]);

  const handleUpdate = async (values: typeof initialEditValues) => {
    if (!gasCilinderId) return;
    await updateBalloon(gasCilinderId, values as any);
    const data = await getBalloonById(gasCilinderId);
    setBalloon(data?.balloon ?? data ?? null);
    setIsEditing(false);
  };

  return (
    <>
      <div className="overflow-y-auto w-full h-auto">
        {!isEditing ? (
          <>
            <div className="flex flex-row justify-between w-full mt-[30px] max-h-[40px] items-center max-w-[1585px] px-[10px]">
              <span className="font-medium text-[18px] text-nowrap">
                {t("GasCilinder.title", { id: balloon?.factoryNumber })}
              </span>
              <div className="flex flex-row gap-[20px] text-wrap">
                <button
                  className="bg-green hover:brightness-90 text-white font-bold py-2 px-4 rounded"
                  type="button"
                  onClick={() => setIsEditing(true)}
                  disabled={!balloon}
                >
                  {t("Редактировать")}
                </button>
                <button
                  className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
                  type="button"
                  onClick={() => navigate(`/gasCilindersList`)}
                >
                  {t("mainButtons.close")}
                </button>
              </div>
            </div>

            <div className="flex flex-col w-full mt-[20px]">
              <div className="bg-white pt-[10px] px-[10px] rounded-xl shadow-md max-w-[1585px]">
                <div className="flex flex-wrap gap-[16px] py-[16px] mb-[16px]">
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.factoryNumber")}</label>
                    <span>{balloon?.factoryNumber ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.uniqueCode")}</label>
                    <span>{balloon?.balloonCode ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.emptyWeightKg")}</label>
                    <span>{balloon?.emptyWeight ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.fullWeightKg")}</label>
                    <span>{balloon?.gasWeight ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.material")}</label>
                    <span>{balloon?.material == "STEEL" ? "Сталь" : "Композит"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.manufactureDate")}</label>
                    <span>{balloon?.productionDate ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.countryOfOrigin")}</label>
                    <span>{balloon?.country === "KAZAKHSTAN" ? "Казахстан" : "Другая страна"}</span>
                  </div>
                  <div className="w-full border-t border-gray-200 my-2"></div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.workingPressureMpa")}</label>
                    <span>{balloon?.workingPressure ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.testPressureMpa")}</label>
                    <span>{balloon?.testPressure ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.lastInspectionDate")}</label>
                    <span>{balloon?.lastInspectionDate ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.nextInspectionDate")}</label>
                    <span>{balloon?.nextInspectionDate ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-[calc(33%-16px)]">
                    <label className="font-light text-gray-600">{t("GasCilinder.lastRepairDate")}</label>
                    <span>{balloon?.lastRepairDate ?? "-"}</span>
                  </div>
                  <div className="flex flex-col w-1/4">
                    <label className="font-light text-gray-600">{t("GasCilinder.repairResponsibleFio")}</label>
                    <span>{balloon?.repairResponsible ?? "-"}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <Formik
            initialValues={initialEditValues}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
            enableReinitialize={true}
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={true}
          >
            {(formik) => (
              <Form className="flex flex-col w-full">
                <div className="flex flex-row justify-between w-full mt-[30px] max-h-[40px] items-center max-w-[1585px] px-[10px]">
                  <span className="font-medium text-[18px] text-nowrap">
                    {t("GasCilinder.title", { id: balloon?.factoryNumber })}
                  </span>
                  <div className="flex flex-row gap-[20px] text-wrap">
                    <button
                      className="bg-green hover:brightness-90 text-white font-bold py-2 px-4 rounded"
                      type="submit"
                      disabled={formik.isSubmitting}
                    >
                      {t("mainButtons.save", { defaultValue: "Сохранить" })}
                    </button>
                    <button
                      className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
                      type="button"
                      onClick={() => setIsEditing(false)}
                    >
                      {t("mainButtons.cancel", { defaultValue: "Отмена" })}
                    </button>
                    <button
                      className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
                      type="button"
                      onClick={() => navigate(`/gasCilindersList`)}
                    >
                      {t("mainButtons.close")}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col w-full mt-[20px]">
                  <div className="bg-white pt-[10px] px-[10px] rounded-xl shadow-md max-w-[1585px]">
                    <div className="flex flex-wrap gap-[16px] py-[16px]">
                      <div className={`${style.inputField} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Заводской номер бытового баллона*</label>
                          {(formik.touched.factoryNumber || formik.submitCount > 0) && formik.errors.factoryNumber && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.factoryNumber === "string" ? formik.errors.factoryNumber : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.factoryNumber || formik.submitCount > 0) && formik.errors.factoryNumber ? "-error" : ""}`}
                          type="number"
                          name="factoryNumber"
                          placeholder="Введите значение"
                          disabled
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Уникальный код баллона(на металлической бирке)*</label>
                          {(formik.touched.balloonCode || formik.submitCount > 0) && formik.errors.balloonCode && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.balloonCode === "string" ? formik.errors.balloonCode : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.balloonCode || formik.submitCount > 0) && formik.errors.balloonCode ? "-error" : ""}`}
                          type="number"
                          name="balloonCode"
                          placeholder="Введите значение"
                          disabled
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Масса пустого баллона , в кг*</label>
                          {(formik.touched.emptyWeight || formik.submitCount > 0) && formik.errors.emptyWeight && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.emptyWeight === "string" ? formik.errors.emptyWeight : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.emptyWeight || formik.submitCount > 0) && formik.errors.emptyWeight ? "-error" : ""}`}
                          type="number"
                          step="0.1"
                          name="emptyWeight"
                          placeholder="Введите значение"
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Масса баллона с газом, в кг*</label>
                          {(formik.touched.gasWeight || formik.submitCount > 0) && formik.errors.gasWeight && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.gasWeight === "string" ? formik.errors.gasWeight : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.gasWeight || formik.submitCount > 0) && formik.errors.gasWeight ? "-error" : ""}`}
                          type="number"
                          step="0.1"
                          name="gasWeight"
                          placeholder="Введите значение"
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Материал баллона*</label>
                          {(formik.touched.material || formik.submitCount > 0) && formik.errors.material && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.material === "string" ? formik.errors.material : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          component={SelectCustomFormik}
                          name="material"
                          placeholder="Выберите"
                          size="sm"
                          isMulti={false}
                          options={[
                            { value: "STEEL", label: "Сталь" },
                            { value: "COMPOSITE", label: "Композит" },
                          ]}
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Дата производства*</label>
                          {(formik.touched.productionDate || formik.submitCount > 0) && formik.errors.productionDate && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.productionDate === "string" ? formik.errors.productionDate : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <CalendarSelectFormik
                          name="productionDate"
                          documentStatus="new"
                          outputFormat="yyyy-MM"
                          showMonthYearSelect={true}
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Страна производства</label>
                          {(formik.touched.country || formik.submitCount > 0) && formik.errors.country && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.country === "string" ? formik.errors.country : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          component={SelectCustomFormik}
                          name="country"
                          placeholder="Выберите"
                          size="sm"
                          isMulti={false}
                          options={[
                            { value: "KAZAKHSTAN", label: "Казахстан" },
                            { value: "OTHER", label: "Другая страна" },
                          ]}
                        />
                      </div>

                      <div className="w-full border-t border-gray-200 my-2"></div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Рабочее давление, в МПа*</label>
                          {(formik.touched.workingPressure || formik.submitCount > 0) && formik.errors.workingPressure && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.workingPressure === "string" ? formik.errors.workingPressure : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.workingPressure || formik.submitCount > 0) && formik.errors.workingPressure ? "-error" : ""}`}
                          type="number"
                          step="0.1"
                          name="workingPressure"
                          placeholder="Введите значение"
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Испытательное давление, в МПа*</label>
                          {(formik.touched.testPressure || formik.submitCount > 0) && formik.errors.testPressure && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.testPressure === "string" ? formik.errors.testPressure : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${(formik.touched.testPressure || formik.submitCount > 0) && formik.errors.testPressure ? "-error" : ""}`}
                          type="number"
                          step="0.1"
                          name="testPressure"
                          placeholder="Введите значение"
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Ф.И.О. ответственного за ремонт</label>
                          {formik.touched.repairResponsible && formik.errors.repairResponsible && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.repairResponsible === "string" ? formik.errors.repairResponsible : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <Field
                          className={`${style.placeholder} ${formik.touched.repairResponsible && formik.errors.repairResponsible ? "-error" : ""}`}
                          type="text"
                          name="repairResponsible"
                          placeholder="Введите значение"
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Дата последней проверки(ТО) (месяц,год)*</label>
                          {(formik.touched.lastInspectionDate || formik.submitCount > 0) && formik.errors.lastInspectionDate && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.lastInspectionDate === "string" ? formik.errors.lastInspectionDate : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <CalendarSelectFormik
                          name="lastInspectionDate"
                          documentStatus="new"
                          outputFormat="yyyy-MM"
                          showMonthYearSelect={true}
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Дата следующей проверки(ТО) (месяц,год)*</label>
                          {(formik.touched.nextInspectionDate || formik.submitCount > 0) && formik.errors.nextInspectionDate && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.nextInspectionDate === "string" ? formik.errors.nextInspectionDate : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <CalendarSelectFormik
                          name="nextInspectionDate"
                          documentStatus="new"
                          outputFormat="yyyy-MM"
                          showMonthYearSelect={true}
                          allowFuture={true}
                        />
                      </div>

                      <div className={`${style.inputField1} w-full md:w-1/2 lg:w-1/4`}>
                        <div className={style.labelAndButtonWrapper}>
                          <label>Дата последнего ремонта(день,месяц,год)</label>
                          {formik.touched.lastRepairDate && formik.errors.lastRepairDate && (
                            <div className={style.tooltipWrapper}>
                              <FaExclamationCircle className={style.errorIcon} />
                              <div className={style.tooltipText}>
                                {typeof formik.errors.lastRepairDate === "string" ? formik.errors.lastRepairDate : "Ошибка"}
                              </div>
                            </div>
                          )}
                        </div>
                        <CalendarSelectFormik
                          name="lastRepairDate"
                          documentStatus="new"
                          showMonthYearSelect={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </>
  );
}

export default GasCilinder;


